'use client';

import React from 'react';
import { useState, useRef, useEffect } from 'react';
import Flickity from 'flickity';
import 'flickity/css/flickity.css';
import { useWindowDimensions } from '@/hooks/use-window-dimensions';
import { ImageGalleryProps, Slide } from '../image-gallery/types';
import ImageGridView from './ImageGridView';
import SliderView from './SliderView';

const ImageGallery: React.FC<ImageGalleryProps> = ({
  images = [
    {
      src: 'https://via.placeholder.com/800x600?text=Test+Image+1',
      alt: 'Test Image 1',
    },
    {
      src: 'https://via.placeholder.com/400x400?text=Test+Image+2',
      alt: 'Test Image 2',
    },
    {
      src: 'https://via.placeholder.com/400x400?text=Test+Image+3',
      alt: 'Test Image 3',
    },
    {
      src: 'https://via.placeholder.com/400x400?text=Test+Image+4',
      alt: 'Test Image 4',
    },
    {
      src: 'https://via.placeholder.com/400x400?text=Test+Image+5',
      alt: 'Test Image 5',
    },
  ],
  virtualTourAvailable = true,
  virtualTourUrl = 'https://my.matterport.com/show/?m=KvNA2ohx2xb&brand=0&play=1&help=0&hr=0&hl=0&gt=1&wh=0&vr=0&qs=1&ts=1&mls=1',
  houseData,
}) => {
  const placeholderImage = `https://placehold.co/1920x1080/e9ecef/495057?text=Image`;
  const [featuredImage, setFeaturedImage] = useState(0);
  const [isSliderMode, setIsSliderMode] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);
  const flickityInstance = useRef<any>(null);
  const { screenIsLargeOrEqualThan } = useWindowDimensions();
  const isMobile = !screenIsLargeOrEqualThan('sm');
  const autoOpenedByMobile = useRef(false);

  // Prepare slides data: VR tour first, then images
  const slides: Slide[] = virtualTourAvailable
    ? [
        { type: 'vr' as const, url: virtualTourUrl },
        ...images.map((img) => ({ type: 'image' as const, ...img })),
        { type: 'map' as const },
      ]
    : [...images.map((img) => ({ type: 'image' as const, ...img })), { type: 'map' as const }];

  // Set slider mode automatically on mobile
  useEffect(() => {
    if (isMobile && !isSliderMode) {
      setIsSliderMode(true);
      autoOpenedByMobile.current = true;
    }
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile && isSliderMode && autoOpenedByMobile.current) {
      setIsSliderMode(false);
      autoOpenedByMobile.current = false;
    }
  }, [isMobile, isSliderMode]);

  // Initialize Flickity when slider mode is activated
  useEffect(() => {
    if (isSliderMode && sliderRef.current && !flickityInstance.current) {
      flickityInstance.current = new Flickity(sliderRef.current, {
        initialIndex: currentSlide,
        wrapAround: true,
        prevNextButtons: false,
        pageDots: false,
        draggable: true,
        cellAlign: 'left',
        contain: true,
        adaptiveHeight: true,
        freeScroll: false,
        groupCells: false,
        autoPlay: false,
        lazyLoad: true,
        imagesLoaded: true,
      });
    }

    return () => {
      if (flickityInstance.current) {
        flickityInstance.current.destroy();
        flickityInstance.current = null;
      }
    };
  }, [isSliderMode, currentSlide]);

  // Handle slide selection
  const handleSlideSelect = (index: number) => {
    if (flickityInstance.current) {
      flickityInstance.current.select(index);
      setSelectedTabIndex(index);
    }
  };

  const nextSlide = () => {
    if (flickityInstance.current) {
      flickityInstance.current.next();
      setSelectedTabIndex(flickityInstance.current.selectedIndex);
    }
  };

  const prevSlide = () => {
    if (flickityInstance.current) {
      flickityInstance.current.previous();
      setSelectedTabIndex(flickityInstance.current.selectedIndex);
    }
  };

  const openSlider = (initialSlide = 0) => {
    setCurrentSlide(initialSlide);
    setSelectedTabIndex(initialSlide);
    setIsSliderMode(true);
    autoOpenedByMobile.current = false;
  };

  const closeSlider = () => {
    setIsSliderMode(false);
  };

  const openVirtualTour = () => {
    setCurrentSlide(0); // First slide is Virtual Tour
    setIsSliderMode(true);
  };

  useEffect(() => {
    if (!isSliderMode) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeSlider();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSliderMode]);

  useEffect(() => {
    if (isSliderMode && !isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isSliderMode, isMobile]);

  return (
    <>
      {!isSliderMode ? (
        <ImageGridView
          images={images}
          virtualTourAvailable={virtualTourAvailable}
          onOpenSlider={openSlider}
          onOpenVirtualTour={openVirtualTour}
          placeholderImage={placeholderImage}
        />
      ) : isMobile ? (
        <SliderView
          slides={slides}
          sliderRef={sliderRef}
          isMobile={isMobile}
          selectedTabIndex={selectedTabIndex}
          onCloseSlider={closeSlider}
          onPrevSlide={prevSlide}
          onNextSlide={nextSlide}
          onTabSelect={handleSlideSelect}
          virtualTourAvailable={virtualTourAvailable}
          houseData={houseData}
          placeholderImage={placeholderImage}
          fullscreen={false}
        />
      ) : (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <SliderView
            slides={slides}
            sliderRef={sliderRef}
            isMobile={isMobile}
            selectedTabIndex={selectedTabIndex}
            onCloseSlider={closeSlider}
            onPrevSlide={prevSlide}
            onNextSlide={nextSlide}
            onTabSelect={handleSlideSelect}
            virtualTourAvailable={virtualTourAvailable}
            houseData={houseData}
            placeholderImage={placeholderImage}
            fullscreen={true}
          />
        </div>
      )}
    </>
  );
};

export default ImageGallery;
